import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxios from './useAxios';
import Constants from '../Constants';
import { toaster } from '../utils/toast';

const APPROVE_SUCCESS = 'Plan updated.';
const APPROVE_FAILURE = 'Failed to approve the plan.';

// drives the Approve action in the Lifecare Studio preview: replace-confirm modal,
// the synchronous promote request, and the screen-blocking state
const useLifecareStudioApprove = ({ caseId, selectedClientId, selectedClient, refetchCaseDetails }) => {
  const { postData } = useAxios();
  const navigate = useNavigate();
  const [isApproving, setIsApproving] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);

  const hasPreviewPlan = Boolean(selectedClient?.result?.lifecarePlan?.s3FilePath);
  const hasCertifiedFile = Boolean(selectedClient?.damage?.certifiedLifecarePlan?.length);
  const isApproveDisabled = !selectedClientId || !hasPreviewPlan || isApproving;

  const runApprove = useCallback(async () => {
    // close the confirm modal up front so only the blocking overlay shows during the request
    setIsReplaceModalOpen(false);
    setIsApproving(true);
    const response = await postData(
      `${Constants.ApiUrl.certifiedLifecarePlan.approve}/${caseId}`,
      { clientId: selectedClientId }
    );
    setIsApproving(false);

    if (response?.success === false) {
      toaster({ message: response?.message || APPROVE_FAILURE, success: false });
      return;
    }
    toaster({ message: APPROVE_SUCCESS, success: true });
    refetchCaseDetails?.();
    navigate(`/case/finalize/${caseId}`);
  }, [caseId, selectedClientId, postData, refetchCaseDetails, navigate]);

  const requestApprove = useCallback(() => {
    if (isApproveDisabled) return;
    if (hasCertifiedFile) {
      setIsReplaceModalOpen(true);
      return;
    }
    runApprove();
  }, [isApproveDisabled, hasCertifiedFile, runApprove]);

  const cancelApprove = useCallback(() => setIsReplaceModalOpen(false), []);

  return useMemo(
    () => ({
      isApproving,
      isApproveDisabled,
      isReplaceModalOpen,
      requestApprove,
      confirmApprove: runApprove,
      cancelApprove,
    }),
    [isApproving, isApproveDisabled, isReplaceModalOpen, requestApprove, runApprove, cancelApprove]
  );
};

export default useLifecareStudioApprove;
